import React, { useState } from 'react';
import Selectors from 'src/utils/selectors';
import SummaryStat from 'src/components/atomic/molecules/summaryStat/SummaryStat';
import LiteralsService from 'src/services/literals/LiteralsService';
import FormatService from 'src/services/format.service';
import adobeTags from 'src/types/adobe-tags';
import { useBudget } from 'src/hooks/queries/budgets';
import { BUDGET_OVERALL_ID } from 'src/services/budget.service';
import { useSpendingSumForCategory } from 'src/hooks/queries/spending';
import DateUtils from 'src/utils/date';
import { PageTimePeriod } from 'src/types/page-time-period.enum';
import TargetMonthlySpendingModal from 'src/components/atomic/organisms/targetMonthlySpendingModal/TargetMonthlySpendingModal';
import TargetMonthlySpendingCallout from 'src/components/atomic/molecules/targetMonthlySpendingCallout/TargetMonthlySpendingCallout';
import StatsBox from 'src/components/atomic/organisms/statsBox/StatsBox';
import { trackClick } from 'src/hooks/useAdobeTags';
import { useLocation } from 'react-router';
import statsBoxStyles from 'src/components/atomic/organisms/statsBox/statsBox.module.scss';

export default function TargetMonthlySpendingStatsBox(): JSX.Element {
    const { pathname } = useLocation();
    const { isLoading: isLoadingTargetMonthlySpending, data: targetMonthlySpending } = useBudget(BUDGET_OVERALL_ID);
    const { isLoading: isLoadingSpendingSums, data: totalSpent } = useSpendingSumForCategory({
        id: BUDGET_OVERALL_ID,
        startDate: DateUtils.getDatesFromTimePeriod(PageTimePeriod.CURRENT_MONTH).startDate,
        endDate: DateUtils.getDatesFromTimePeriod(PageTimePeriod.CURRENT_MONTH).endDate
    });
    const [openTargetMonthlySpendingModal, setOpenTargetMonthlySpendingModal] = useState<boolean>(false);

    if (isLoadingTargetMonthlySpending || isLoadingSpendingSums) return <></>;

    const overallRemaining: number =
        targetMonthlySpending?.monthlyAmount - totalSpent?.spent > 0
            ? targetMonthlySpending?.monthlyAmount - totalSpent?.spent
            : 0;

    const openModal = (): void => setOpenTargetMonthlySpendingModal(true);

    return (
        <>
            {openTargetMonthlySpendingModal && (
                <TargetMonthlySpendingModal closeModal={() => setOpenTargetMonthlySpendingModal(false)} />
            )}
            {targetMonthlySpending?.monthlyAmount ? (
                <StatsBox data-cy={Selectors.SpendSmart.TARGET_MONTHLY_SPENDING_STATS_BOX}>
                    <SummaryStat
                        title={LiteralsService.getLiteral(
                            'Budget.Current.targetMonthlySpendingStatsBox.targetMonthlySpendingTitle'
                        )}
                        tooltip={LiteralsService.getLiteral(
                            'Budget.Current.targetMonthlySpendingStatsBox.targetMonthlySpendingTooltip'
                        )}
                        amount={FormatService.formatCurrency(targetMonthlySpending?.monthlyAmount, { round: true })}
                        tagName={adobeTags.Tooltip.TargetStatsBox.Target}
                        onClick={() => {
                            trackClick(pathname, adobeTags.CurrentMonth.TargetStatsBoxEdit);
                            openModal();
                        }}
                        iconTitle={LiteralsService.getLiteral(
                            'Budget.Current.targetMonthlySpendingStatsBox.editIconTitle'
                        )}
                    />
                    <SummaryStat
                        title={LiteralsService.getLiteral(
                            'Budget.Current.targetMonthlySpendingStatsBox.totalSpentTitle'
                        )}
                        tooltip={LiteralsService.getLiteral(
                            'Budget.Current.targetMonthlySpendingStatsBox.totalSpentTooltip'
                        )}
                        amount={FormatService.formatCurrency(totalSpent?.spent)}
                        tagName={adobeTags.Tooltip.TargetStatsBox.TotalSpent}
                    />
                    <SummaryStat
                        title={LiteralsService.getLiteral('Budget.Current.targetMonthlySpendingStatsBox.leftOverTitle')}
                        tooltip={LiteralsService.getLiteral(
                            'Budget.Current.targetMonthlySpendingStatsBox.leftOverTooltip'
                        )}
                        amount={FormatService.formatCurrency(overallRemaining)}
                        tooltipClassName={statsBoxStyles.thirdTooltip}
                        isGreen={overallRemaining > 0}
                        tagName={adobeTags.Tooltip.TargetStatsBox.LeftOver}
                    />
                </StatsBox>
            ) : (
                <TargetMonthlySpendingCallout
                    onClick={() => {
                        openModal();
                        trackClick(pathname, adobeTags.General.SetTarget);
                    }}
                />
            )}
        </>
    );
}
